import StorySource from '../../data/story-source';
import LocalStorage from '../../data/local-storage';
import CONFIG from '../../config/app-config';
import AuthService from '../../services/auth-service';
import BookmarkService from '../../services/bookmark-service';
import NotificationService from '../../services/notification-service';
import {
  getLocationName as fetchLocationName,
  searchLocation as searchLocations,
  getUserLocation as detectUserLocation,
  getMapStyleUrl,
  buildMapConfig
} from '../../services/location-service';

export default class HomeModel {
  constructor() {
    this.stories = [];
    this.currentOffset = 0;
    this.hasMorePosts = true;
    this.isAuthenticated = false;
    this.user = {};
    this.bookmarks = [];
    this.notifications = [];
    this.currentUserLocation = null;
    this.nearbyDistance = 500;
    this.currentMapLayer = 'streets';
    this.lastFetchTime = null;
    this.newPostsAvailable = 0;
    this.selectedImage = null;
    this.selectedLocation = null;
    this.videoDevices = [];
    this.currentCameraIndex = 0;
  }

  loadUserState() {
    const { isAuthenticated, user } = AuthService;
    this.isAuthenticated = isAuthenticated;
    this.user = user || {};
    this.bookmarks = BookmarkService.getBookmarks();
    this.notifications = NotificationService.getNotifications();

    if (isAuthenticated && user.id) {
      this.currentUserLocation = LocalStorage.getUserSpecificObject(
        'heliaedu_user_location',
        user.id
      );
      this.nearbyDistance = parseInt(
        LocalStorage.getOrSetUserSpecificItem('heliaedu_user_distance', user.id, '500')
      );
      this.currentMapLayer = LocalStorage.getOrSetUserSpecificItem(
        'heliaedu_user_map_layer',
        user.id,
        'streets'
      );
      this.lastFetchTime = LocalStorage.getItem('heliaedu_user_lastfetch');
    } else {
      this.currentUserLocation = null;
      this.bookmarks = [];
      this.notifications = [];
      this.nearbyDistance = parseInt(
        LocalStorage.getOrSetItem('heliaedu_guest_distance', '500')
      );
      this.currentMapLayer = LocalStorage.getOrSetItem('heliaedu_guest_map_layer', 'streets');
    }
  }

  flipCamera() {
    if (this.videoDevices.length > 1) {
      this.currentCameraIndex = (this.currentCameraIndex + 1) % this.videoDevices.length;
    }
  }

  async getAllStories(params = {}) {
    const response = await StorySource.getAllStories(params);
    if (!response.error) {
      return response.listStory || [];
    }
    throw new Error(response.message || 'Failed to fetch stories');
  }

  async addStory(storyData) {
    const response = await StorySource.addStory(storyData);
    if (!response.error) {
      return response;
    }
    throw new Error(response.message || 'Failed to add story');
  }

  async getLocationName(lat, lon) {
    return fetchLocationName(lat, lon);
  }

  async searchLocation(query) {
    return searchLocations(query);
  }

  async getUserLocation() {
    return detectUserLocation();
  }

  async prepareCamerasForDialog() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error('Browser tidak mendukung enumerasi perangkat');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      this.videoDevices = devices.filter((device) => device.kind === 'videoinput');

      this.currentCameraIndex = 0;

      if (this.videoDevices.length > 1) {
        const rearCameraIndex = this.videoDevices.findIndex(
          (device) =>
            device.label.toLowerCase().includes('back') ||
            device.label.toLowerCase().includes('rear')
        );
        if (rearCameraIndex !== -1) {
          this.currentCameraIndex = rearCameraIndex;
        }
      }

      return this.videoDevices;
    } catch (err) {
      throw new Error('Gagal mengakses kamera: ' + err.message);
    }
  }

  async getUserMediaStream(deviceIndex = this.currentCameraIndex) {
    if (this.videoDevices.length === 0) {
      throw new Error('Tidak ada kamera yang tersedia');
    }

    if (deviceIndex >= this.videoDevices.length) {
      deviceIndex = 0;
    }

    const selectedDevice = this.videoDevices[deviceIndex];

    const constraints = {
      video: {
        deviceId: { exact: selectedDevice.deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    };

    return await navigator.mediaDevices.getUserMedia(constraints);
  }

  getMapStyleConfig(isDark = false, layerType = this.currentMapLayer || 'streets') {
    return getMapStyleUrl(isDark, layerType);
  }

  getLocationMapConfig(isDark = false) {
    return buildMapConfig('location-map', isDark, this.currentMapLayer);
  }

  getMapLayerKey() {
    return this.isAuthenticated && this.user.id
      ? LocalStorage.getUserSpecificKey('heliaedu_user_map_layer', this.user.id)
      : 'heliaedu_guest_map_layer';
  }

  setCurrentMapLayer(layerType) {
    this.currentMapLayer = layerType;
    LocalStorage.setItem(this.getMapLayerKey(), layerType);
  }

  getCurrentMapLayer() {
    return this.currentMapLayer;
  }

  addBookmark(storyId) {
    const story = this.stories.find((s) => s.id === storyId);

    if (story) {
      const bookmark = BookmarkService.addBookmark(story);
      this.bookmarks = BookmarkService.getBookmarks();
      return bookmark;
    }
    return null;
  }

  removeBookmark(storyId) {
    const count = BookmarkService.removeBookmark(storyId);
    this.bookmarks = BookmarkService.getBookmarks();
    return count > 0;
  }

  isBookmarked(storyId) {
    return BookmarkService.getBookmarks().some((b) => b.storyId === storyId);
  }

  updateLastFetchTime() {
    this.lastFetchTime = new Date().toISOString();
    LocalStorage.setItem('heliaedu_user_lastfetch', this.lastFetchTime);
  }

  checkForNewPosts(stories) {
    if (!this.lastFetchTime || !this.isAuthenticated) return 0;

    const lastFetchDate = new Date(this.lastFetchTime);
    const newStories = stories.filter((story) => new Date(story.createdAt) > lastFetchDate);

    this.newPostsAvailable = newStories.length;
    return this.newPostsAvailable;
  }

  setStories(stories) {
    this.stories = stories;
  }

  appendStories(stories) {
    this.stories = [...this.stories, ...stories];
  }

  setSelectedImage(file) {
    this.selectedImage = file;
  }

  getSelectedImage() {
    return this.selectedImage;
  }

  clearSelectedImage() {
    this.selectedImage = null;
  }

  setSelectedLocation(location) {
    this.selectedLocation = location;
  }

  getSelectedLocation() {
    return this.selectedLocation;
  }

  clearSelectedLocation() {
    this.selectedLocation = null;
  }

  getFormData(description) {
    return {
      description,
      photo: this.selectedImage,
      lat: this.selectedLocation ? this.selectedLocation.lat : null,
      lon: this.selectedLocation ? this.selectedLocation.lon : null,
    };
  }


  clearForm() {
    this.selectedImage = null;
    this.selectedLocation = null;
  }

  updateCurrentOffset(offset) {
    this.currentOffset = offset;
  }

  setHasMorePosts(hasMore) {
    this.hasMorePosts = hasMore;
  }

  resetPagination() {
    this.currentOffset = 0;
    this.hasMorePosts = true;
  }
}
